import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import TextInput from "../../components/forms/TextInput";
import { useAuth } from "../../js/hooks/useAuth";
import { defaultSeatTierId } from "../../js/auth/packageAccess";
import { SEAT_TIERS } from "../../js/utils/siteLinks";
import { resolvePostAuthPath } from "../../js/auth/onboardingState";
import {
  validateAgentAccount,
  validateBrokerAccount,
  validateBuyerAccount,
  validateSellerAccount,
} from "../../js/utils/validators";

const ROLES = [
  { id: "agent", label: "Agent" },
  { id: "broker", label: "Broker" },
  { id: "buyer", label: "Buyer" },
  { id: "seller", label: "Seller" },
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  password: "",
  agent: "",
  broker: "",
  office: "",
  tier: defaultSeatTierId(),
};

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const initialRole = ROLES.some((item) => item.id === params.get("role")) ? params.get("role") : "agent";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    setRole(initialRole);
  }, [initialRole]);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function validate() {
    if (role === "agent") return validateAgentAccount(form);
    if (role === "broker") return validateBrokerAccount(form);
    if (role === "seller") return validateSellerAccount(form);
    return validateBuyerAccount(form);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      const result = await register({ ...form, role });
      navigate(resolvePostAuthPath(result.session, role));
    } catch (error) {
      setMessage(error.message);
    }
  }

  const loginHref =
    role === "broker"
      ? "/broker/login"
      : role === "agent"
        ? "/agent/login"
        : role === "seller"
          ? "/seller/login"
          : "/login";

  return (
    <AppLayout>
      <div className="card auth-card">
        <h1>Create an Account</h1>
        <p className="page-lead">
          {role === "agent" || role === "broker"
            ? "Create your account, verify email, then activate your desk subscription before your first dashboard load."
            : "Buyers and sellers stay linked to their agent and office — no desk subscription of their own."}
        </p>
        <nav className="messages-desk__tabs" aria-label="Account type">
          {ROLES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`messages-tab${role === item.id ? " is-active" : ""}`}
              onClick={() => {
                setRole(item.id);
                setErrors({});
                setMessage("");
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <form onSubmit={handleSubmit}>
          <TextInput label="Full name" name="name" value={form.name} onChange={update} error={errors.name} />
          <TextInput label="Email" name="email" type="email" value={form.email} onChange={update} error={errors.email} />
          {role === "buyer" || role === "seller" || role === "agent" ? (
            <TextInput label="Phone" name="phone" value={form.phone} onChange={update} error={errors.phone} />
          ) : null}
          <TextInput
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={update}
            autoComplete="new-password"
          />
          {role === "agent" || role === "buyer" || role === "seller" ? (
            <TextInput
              label={role === "agent" ? "Broker" : "Your agent"}
              name={role === "agent" ? "broker" : "agent"}
              value={role === "agent" ? form.broker : form.agent}
              onChange={update}
              error={role === "agent" ? errors.broker : errors.agent}
              hint={role === "agent" ? "Name or email of your supervising broker." : "Name or email."}
            />
          ) : null}
          {role === "buyer" || role === "seller" ? (
            <TextInput
              label="Your broker"
              name="broker"
              value={form.broker}
              onChange={update}
              error={errors.broker}
            />
          ) : null}
          <TextInput
            label="Office"
            name="office"
            value={form.office}
            onChange={update}
            error={errors.office}
          />
          {role === "broker" ? (
            <label className="field">
              <span className="field-label">Seat plan</span>
              <select className="field-select" name="tier" value={form.tier} onChange={update}>
                {Object.values(SEAT_TIERS).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.tier ? <span className="field-error">{errors.tier}</span> : null}
            </label>
          ) : null}
          {message ? <p className="field-error">{message}</p> : null}
          <button type="submit" className="primary-btn btn-block">
            {role === "agent" || role === "broker" ? "Create account & continue" : "Create account"}
          </button>
        </form>
        <p className="muted">
          Already have an account? <Link to={loginHref}>Sign In</Link>
        </p>
      </div>
    </AppLayout>
  );
}
